import { Component, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GridOptions, GridApi, ColDef } from 'ag-grid-community';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { TreeNode } from 'primeng/api';
import { CommonService } from 'src/app/shared/services/common.service';
import { LocalStorageSettingsService } from 'src/app/shared/services/local-storage-settings.service';
import { MeddraBasketCreationComponent } from '../meddra-basket-creation/meddra-basket-creation.component';
import { CustomCheckButton } from 'src/app/shared/common/Checkbutton';
import { JSIGNALBUTTON } from 'src/app/shared/common/JsignalButtons';
import { JsignalService } from 'src/app/shared/services/jsignal.service';

// ==================== INTERFACES ====================
interface BasketTerm {
  type: string;
  level?: string;
  code: string;
  name: string;
  provenance: string;
  scope?: string;
}

interface SMQHierarchyNode {
  code: string;
  name: string;
  parent_code: string;
  has_hierarchy: string;
  scope?: string;
  definition?: string;
  children: SMQHierarchyNode[];
  leaf?: boolean;
}

interface SMQSearchRequest {
  filterMap?: {
    parent_query__v?: string;
    scope__v?: string;
    [key: string]: string | undefined;
  };
  languageOption: string;
  page?: number;
  medDRAVersion: string;
}

interface SMQTreeNode extends TreeNode {
  loading: boolean;
  data: {
    code: string;
    name: string;
    has_hierarchy: string;
    scope?: string;
    definition?: string;
    type: 'smq' | 'term';
    level?: string;
    hierarchical?: boolean;
    isSearchResult?: boolean;
    searchMatch?: boolean;
    fullRecord?: any;
  };
}

// ==================== CONSTANTS ====================
const EXTRACTION_LEVEL_OPTIONS = [
  { label: 'SOC Level', value: 'soc' },
  { label: 'HLGT Level', value: 'hlgt' },
  { label: 'HLT Level', value: 'hlt' },
  { label: 'PT Level', value: 'pt' },
  { label: 'LLT Level', value: 'llt' }
];

const LANGUAGE_OPTIONS = [
  { name: 'English', code: 'EN' },
  { name: 'Japanese', code: 'JP' }
];

const SEARCH_LEVEL_OPTIONS = [
  { label: 'SOC', value: 'soc' },
  { label: 'HLGT', value: 'hlgt' },
  { label: 'HLT', value: 'hlt' },
  { label: 'PT', value: 'pt' },
  { label: 'LLT', value: 'llt' }
];

const FILTER_OPTIONS = [
  { label: 'Primary SOC Only', value: 'primarySOCOnly' },
  { label: 'Include Non Current', value: 'includeNonCurrent' },
  { label: 'Display Codes', value: 'displayCodes' }
];

const LEVEL_FIELD_MAP: Record<string, { term: string; code: string }> = {
  soc:  { term: "socTerm",  code: "socCode" },
  hlgt: { term: "hlgtTerm", code: "hlgtCode" },
  hlt:  { term: "hltTerm",  code: "hltCode" },
  pt:   { term: "ptTerm",   code: "ptCode" },
  llt:  { term: "lltTerm",  code: "lltCode" }
};

const BACKEND_LEVEL_MAP: Record<string, string> = {
  'SOC': 'soc__v',
  'HLGT': 'hlgt__v',
  'HLT': 'hlt__v',
  'PT': 'pt__v',
  'LLT': 'llt__v'
};

const LEVEL_HIERARCHY = ['SOC', 'HLGT', 'HLT', 'PT', 'LLT']; 

// ==================== COMPONENT DEFINITION ====================
@Component({
    selector: 'app-meddra-browser',
    templateUrl: './meddra-browser.component.html',
    styleUrls: ['./meddra-browser.component.scss'],
    standalone: false
})
export class MeddraBrowserComponent implements OnInit {
  // ==================== UI STATE ====================
  mode: 'select' | 'browse' = 'browse';
  activeTab: 'terms' | 'smqs' | 'cmqs' = 'terms';
  backgroundColorScreen = '#ffffff';
  backgroundColorButton = '#1976d2';
  
  // ==================== DATA STATE ====================
  meddraVersions: any[] = [];
  selectedMeddraVersion: any = null;
  isLatestVersion = true;
  
  treeData: TreeNode[] = [];
  rightTableData: any[] = [];
  filteredTableData: any[] = [];
  BasketData: any[] = [];
  meddraBaskets: any[] = [];
  
  // ==================== SELECTION STATE ====================
  selectedItems: any[] = [];
  selectedTerm: any = null;
  selectedSMQ: any = null;
  selectedLevel: string | null = null;
  selectedName: string | null = null;
  selectedMeddraBasket: any = null;
  
  // ==================== FILTER STATE ====================
  searchQuery = '';
  smqSearchQuery = '';
  cmqSearchQuery = '';
  primarySOCOnly = false;
  includeNonCurrent = false;
  includeSecondarySOC = false;
  showCurrentTerms = true;
  searchValidationError: string = '';
  
  // ==================== CONFIGURATION ====================
  selectedLanguage = LANGUAGE_OPTIONS[0];
  selectedExtractionLevel = 'pt';
  selectedFilters: string[] = [];
  
  extractionLevelOptions = EXTRACTION_LEVEL_OPTIONS;
  languageOptions = LANGUAGE_OPTIONS;
  searchLevelOptions = SEARCH_LEVEL_OPTIONS;
  filterOptions = FILTER_OPTIONS;
  
  // ==================== SMQ STATE ====================
  smqList: any[] = [];
  filteredSMQList: any[] = [];
  smqTreeData: SMQTreeNode[] = [];
  loadingSMQs = false;
  smqHierarchyNodes: SMQHierarchyNode[] = [];
  loadingNode = new Map<string, boolean>();
  expandedSMQNodes = new Set<string>();
  childSMQsData: any[] = [];
  meddraTermsData: any[] = [];
  
  // ==================== CMQ STATE ====================
  loadingCMQs = false;
  filteredCMQList: any[] = [];
  
  // ==================== AG-GRID REFERENCES ====================
  private gridApi!: GridApi;
  private smqChildGridApi!: GridApi;
  private smqTermsGridApi!: GridApi;
  private cmqGridApi!: GridApi;

  columnDefs: ColDef[] = [];
  smqChildColDefs: ColDef[] = [];
  smqTermsColDefs: ColDef[] = [];
  cmqColDefs: ColDef[] = [];

  // Separate grid options for each grid
  termsGridOptions: GridOptions = {};
  smqChildGridOptions: GridOptions = {};
  smqTermsGridOptions: GridOptions = {};
  cmqGridOptions: GridOptions = {};

  components: any;

  // ==================== CONSTRUCTOR ====================
  constructor(
    private localStorage: LocalStorageSettingsService,
    private commonService: CommonService,
    private snackBar: MatSnackBar,
    private spinner: NgxUiLoaderService,
    private dialog: MatDialog,
    private jsignalsService: JsignalService,
    @Inject(MAT_DIALOG_DATA) @Optional() public data: any,
    @Optional() public dialogRef?: MatDialogRef<MeddraBrowserComponent>
  ) {
    this.components = {
      jsignalButton: JSIGNALBUTTON,
      headerRenderer: CustomCheckButton,
    };
  }

  // ==================== LIFECYCLE HOOKS ====================
  ngOnInit(): void {
    this.initializeComponent();
    this.initializeGrid();
    this.loadInitialData();
  }

  // ==================== INITIALIZATION ====================
  private initializeComponent(): void {
    this.mode = this.data?.mode === 'select' ? 'select' : 'browse';
    this.backgroundColorScreen = this.localStorage.getSettings("ColorD") || '#ffffff';
    this.backgroundColorButton = this.localStorage.getSettings("ColorA") || '#1976d2';
  }

  private initializeGrid(): void {
    this.columnDefs = this.createTermsColumnDefs();
    this.cmqColDefs = this.createCMQColumnDefs();
    this.smqChildColDefs = this.createSMQChildColumnDefs();
    this.smqTermsColDefs = this.createSMQTermsColumnDefs();
    
    // Create separate grid options for each grid
    this.initializeTermsGridOptions();
    this.initializeSMQChildGridOptions();
    this.initializeSMQTermsGridOptions();
    this.initializeCMQGridOptions();
  }

  private initializeTermsGridOptions(): void {
    this.termsGridOptions = {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        tooltipComponentParams: { color: '#ececec' },
        autoHeight: true,
        wrapText: true,
        filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      }
      },
      columnDefs: this.columnDefs,
      suppressCellFocus: true,
      suppressRowClickSelection: false,
      rowMultiSelectWithClick: true,
      pagination: true,
      paginationPageSize: 50,
      onGridReady: (params) => {
        this.gridApi = params.api;
        setTimeout(() => {
          params.api.sizeColumnsToFit();
        }, 100);
      },
      onFirstDataRendered: (params) => {
        params.api.sizeColumnsToFit();
        params.api.resetRowHeights();
      },
      onGridSizeChanged: (params) => {
        params.api.sizeColumnsToFit();
      }
    };
  }

  private initializeSMQChildGridOptions(): void {
    this.smqChildGridOptions = {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        tooltipComponentParams: { color: '#ececec' },
        filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      }
      },
      columnDefs: this.smqChildColDefs,
      rowHeight: 40,
      suppressCellFocus: true,
      suppressRowClickSelection: false,
      pagination: true,
      paginationPageSize: 50
    };
  }

  private initializeSMQTermsGridOptions(): void {
    this.smqTermsGridOptions = {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        tooltipComponentParams: { color: '#ececec' },
        filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      }
      },
      columnDefs: this.smqTermsColDefs,
      rowHeight: 40,
      suppressCellFocus: true,
      suppressRowClickSelection: false,
      pagination: true,
      paginationPageSize: 50
    };
  }

  private initializeCMQGridOptions(): void {
    this.cmqGridOptions = {
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        tooltipComponentParams: { color: '#ececec' },
        filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      }
      },
      columnDefs: this.cmqColDefs,
      rowHeight: 40,
      suppressCellFocus: true,
      suppressRowClickSelection: false,
      pagination: true,
      paginationPageSize: 50
      };
  }

  private loadInitialData(): void {
    this.loadMeddraVersions();
  }

  // ==================== DATA LOADING ====================
  private loadMeddraVersions(): void {
    this.spinner.start();
    
    this.commonService.getAvailableVersions().subscribe({
      next: (versions: string[]) => {
        this.processMeddraVersions(versions);
        this.spinner.stop();
      },
      error: (error) => this.handleError('Error loading MedDRA versions', error)
    });
  }

  private processMeddraVersions(versions: string[]): void {
    this.meddraVersions = versions.map(version => ({
      label: version.replace('meddra_', '').replace('__c', '').replace(/_/g, '.'),
      value: version,
    }));

    if (this.meddraVersions.length > 0) {
      this.selectedMeddraVersion = this.meddraVersions[0];
      this.loadSocs();
      this.loadTopLevelSMQs();
      this.loadCMQList();
      this.loadMeddraBaskets();
    }
  }

  private loadSocs(): void {
    this.spinner.start();
    
    const request = this.buildMeddraRequest('soc__v');
    
    this.commonService.getMeddraData(request).subscribe({
      next: (socs: any[]) => {
        this.treeData = socs.map(soc => this.createTreeNode(soc, 'SOC'));
        this.spinner.stop();
      },
      error: (error) => this.handleError('Error loading SOCs', error)
    });
  }

  // ==================== TREE METHODS ====================
  private createTreeNode(record: any, level: string): TreeNode {
    const { label, key } = this.extractLabelAndKey(record, level);
    
    return {
      label: label,
      key: key,
      data: { 
        level: level, 
        code: key,
        term: label,
        fullRecord: record
      },
      leaf: level === 'LLT',
      children: level !== 'LLT' ? [] : undefined
    };
  }

onNodeExpand(event: any): void {
    const node: TreeNode = event.node;
    
    if (node.data?.isSearchResult === true || this.searchQuery?.trim()) {
      return;
    }

    if (node.children && node.children.length > 0) return;

    const nextLevel = this.getNextLevel(node.data?.level);
    const request = this.buildMeddraRequest(
      this.getBackendLevel(nextLevel),
      this.buildFilterMapForNode(node)
    );

    this.spinner.start();
    
    this.commonService.getMeddraData(request).subscribe({
      next: (children: any[]) => {
        const childNodes = children.map(child => 
          this.createTreeNode(child, nextLevel)
        );
        
       
        this.treeData = this.replaceNodeInTree(this.treeData, node.key, childNodes);
        
        this.spinner.stop();
      },
      error: (error) => this.handleError(`Error loading ${nextLevel}s`, error)
    });
  }
  private stripHtmlTags(html: string): string {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  onNodeSelect(event: any): void {
    const node: TreeNode = event.node;
    this.selectedLevel = node.data?.level;
    this.selectedName = this.stripHtmlTags(node.label || '');
    this.selectedTerm = node.data;

    if (node.data?.isSearchResult && node.data?.level === 'LLT' && node.data?.fullRecord) {
      this.processSearchLLTNode(node);
    } else if (node.data?.isSearchResult) {
      this.loadSearchBranchRecords(node);
    } else {
      this.loadNodeRecords(node);
    }
  }

  private processSearchLLTNode(node: TreeNode): void {
    const fullRecord = node.data?.fullRecord;
    if (!fullRecord) return;
    
    this.rightTableData = [{
      llt: fullRecord.llt_term || fullRecord.lltTerm,
      pt: fullRecord.pt_term || fullRecord.ptTerm,
      hlt: fullRecord.hlt_term || fullRecord.hltTerm,
      hlgt: fullRecord.hlgt_term || fullRecord.hlgtTerm,
      soc: fullRecord.soc_term || fullRecord.socTerm,
      code: fullRecord.llt_code || fullRecord.lltCode,
      status: fullRecord.current_flag === 'Y' ? 'Current' : 'Non-current',
      fullRecord: fullRecord
    }];
    
    this.filteredTableData = [...this.rightTableData];
  }

  private loadSearchBranchRecords(node: TreeNode): void {
    const allLLTs: any[] = [];
    
    const collectLLTs = (currentNode: TreeNode) => {
      if (currentNode.data?.level === 'LLT' && currentNode.data?.fullRecord) {
        allLLTs.push(currentNode.data.fullRecord);
      }
      
      if (currentNode.children) {
        currentNode.children.forEach(child => collectLLTs(child));
      }
    };
    
    collectLLTs(node);
    
    this.rightTableData = allLLTs.map(record => ({
      llt: record.llt_term || record.lltTerm,
      pt: record.pt_term || record.ptTerm,
      hlt: record.hlt_term || record.hltTerm,
      hlgt: record.hlgt_term || record.hlgtTerm,
      soc: record.soc_term || record.socTerm,
      code: record.llt_code || record.lltCode,
      status: record.current_flag === 'Y' ? 'Current' : 'Non-current',
      fullRecord: record
    }));
    
    this.filteredTableData = [...this.rightTableData];
  }

  private loadNodeRecords(node: TreeNode): void {
    this.spinner.start();
    
    const request = this.buildMeddraRequest(
      'llt__v',
      this.buildFilterMapForNode(node)
    );

    this.commonService.getMeddraData(request).subscribe({
      next: (records: any[]) => {
        this.processNodeRecords(records);
        this.spinner.stop();
      },
      error: (error) => this.handleError('Error loading records', error)
    });
  }

  private processNodeRecords(records: any[]): void {
    this.rightTableData = records.map(record => ({
      llt: record.lltTerm,
      pt: record.ptTerm,
      hlt: record.hltTerm,
      hlgt: record.hlgtTerm,
      soc: record.socTerm,
      code: record.lltCode,
      status: record.currentFlag === 'Y' ? 'Current' : 'Non-current',
      fullRecord: record
    }));
    
    this.filteredTableData = [...this.rightTableData];
    this.searchQuery = '';
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.resetRowHeights();
        this.gridApi.sizeColumnsToFit();
      }
    }, 100);
  }

  private buildFilterMapForNode(node: TreeNode): any {
    const filterMap: any = {};
    let currentNode = node;
    
    while (currentNode) {
      const level = currentNode.data?.level;
      const key = currentNode.key;
      
      switch (level) {
        case 'SOC': filterMap.soc_code__v = key; break;
        case 'HLGT': filterMap.hlgt_code__v = key; break;
        case 'HLT': filterMap.hlt_code__v = key; break;
        case 'PT': filterMap.pt_code__v = key; break;
        case 'LLT': filterMap.llt_code__v = key; break;
      }
      
      currentNode = currentNode.parent;
    }
    
    return filterMap;
  }

  // ==================== SMQ METHODS ====================
  private loadTopLevelSMQs(): void {
    this.loadingSMQs = true;
    
    const request: SMQSearchRequest = {
      filterMap: { parent_query__v: '' },
      languageOption: this.selectedLanguage.code,
      page: 0,
      medDRAVersion: this.selectedMeddraVersion?.value
    };

    this.jsignalsService.getTopLevelSMQs().subscribe({
      next: (nodes: SMQHierarchyNode[]) => {
        this.smqHierarchyNodes = nodes;
        this.smqTreeData = this.convertSMQToTreeNodes(nodes);
        this.loadingSMQs = false;
        this.updateSMQListFromHierarchy(nodes);
      },
      error: (error) => this.handleError('Error loading SMQs', error)
    });
  }

  private convertSMQToTreeNodes(nodes: SMQHierarchyNode[]): SMQTreeNode[] {
    return nodes.map(node => {
      const isHierarchical = node.has_hierarchy === 'true';
      return {
        label: node.name,
        key: node.code,
        data: {
          code: node.code,
          name: node.name,
          has_hierarchy: node.has_hierarchy,
          hierarchical: isHierarchical,
          scope: node.scope,
          definition: node.definition,
          type: 'smq',
          level: 'SMQ'
        },
        leaf: !isHierarchical,
        expanded: false,
        children: isHierarchical ? [] : undefined,
        loading: false
      } as SMQTreeNode;
    });
  }

  onSMQNodeExpand(event: any): void {
    const node: SMQTreeNode = event.node;
    const nodeData = node.data;
    
    if (!nodeData || nodeData.has_hierarchy !== 'true') {
      return;
    }
    
    if (!node.children || node.children.length === 0) {
      this.loadSMQChildren(node);
    }
  }

  private loadSMQChildren(node: SMQTreeNode): void {
    const nodeData = node.data;
    const smqCode = nodeData.code;
    
    if (!smqCode || nodeData.has_hierarchy !== 'true') return;
    
    node.loading = true;
    this.loadingNode.set(smqCode, true);
    
    const request: SMQSearchRequest = {
      filterMap: { parent_query__v: smqCode },
      languageOption: this.selectedLanguage.code,
      page: 0,
      medDRAVersion: this.selectedMeddraVersion?.value
    };
    
    this.jsignalsService.getSMQChildren(request).subscribe({
      next: (children: SMQHierarchyNode[]) => {
        const childNodes: SMQTreeNode[] = children.map(child => {
          const isHierarchical = child.has_hierarchy === 'true';
          return {
            label: child.name,
            key: child.code,
            data: {
              code: child.code,
              name: child.name,
              has_hierarchy: child.has_hierarchy,
              hierarchical: isHierarchical,
              scope: child.scope,
              definition: child.definition,
              type: 'smq',
              level: 'SMQ'
            },
            leaf: !isHierarchical,
            expanded: false,
            children: isHierarchical ? [] : undefined,
            loading: false
          } as SMQTreeNode;
        });
        
        node.children = childNodes;
        node.loading = false;
        this.loadingNode.delete(smqCode);
        this.expandedSMQNodes.add(smqCode);
        // Replace the node in the tree using SMQ-specific method
            this.smqTreeData = this.replaceSMQNodeInTree(
                this.smqTreeData, 
                node.key, 
                childNodes
            );
      },
      error: (error) => {
        this.handleError('Error loading SMQ children', error);
        node.loading = false;
        this.loadingNode.delete(smqCode);
        node.expanded = false;
        this.expandedSMQNodes.delete(smqCode);
      }
    });
  }

  onSMQNodeSelect(event: any): void {
    const node: SMQTreeNode = event.node;
    const nodeData = node.data;
    
    if (!nodeData) return;
    
    this.selectedSMQ = {
      ...nodeData,
      version: this.selectedMeddraVersion?.label
    };
    
    this.childSMQsData = [];
    this.meddraTermsData = [];
    
    if (nodeData.has_hierarchy === 'true') {
      this.loadChildSMQsForGrid(nodeData.code);
      if (!node.expanded) {
        this.loadSMQChildren(node);
      }
    } else {
      this.loadMeddraTermsForSMQ(nodeData.code);
    }
  }

  private loadChildSMQsForGrid(smqCode: string): void {
    this.spinner.start();
    
    const request: SMQSearchRequest = {
      filterMap: { parent_query__v: smqCode },
      languageOption: this.selectedLanguage.code,
      page: 0,
      medDRAVersion: this.selectedMeddraVersion?.value
    };
    
    this.jsignalsService.getSMQChildren(request).subscribe({
      next: (children: SMQHierarchyNode[]) => {
        this.childSMQsData = children.map(child => ({
          code: child.code,
          name: child.name,
          has_hierarchy: child.has_hierarchy,
          scope: child.scope || 'N/A',
          definition: child.definition || '',
          hierarchical: child.has_hierarchy === 'true'
        }));
        this.spinner.stop();
      },
      error: (error) => this.handleError('Error loading child SMQs', error)
    });
  }

  private loadMeddraTermsForSMQ(smqCode: string): void {
    this.spinner.start();
    
    const request: SMQSearchRequest = {
      filterMap: { smq_code__v: smqCode },
      languageOption: 'EN',
      page: 0,
      medDRAVersion: this.selectedMeddraVersion?.value
    };
    
    this.jsignalsService.getSMQTerms(request).subscribe({
      next: (terms: any[]) => {
        this.meddraTermsData = terms.map(term => ({
          ptCode: term.ptCode || term.code,
          ptName: term.ptTerm || term.name,
          scope: term.scope || 'N/A',
          name: term.name,
          type: 'PT'
        }));
        this.spinner.stop();
      },
      error: (error) => this.handleError('Error loading SMQ terms', error)
    });
  }

  private updateSMQListFromHierarchy(nodes: SMQHierarchyNode[]): void {
    const flatSMQs: any[] = [];
    
    const flattenNodes = (nodeList: SMQHierarchyNode[]) => {
      nodeList.forEach(node => {
        flatSMQs.push({
          code: node.code,
          name: node.name,
          scope: node.scope || 'N/A',
          version: this.selectedMeddraVersion?.label,
          hierarchical: node.has_hierarchy === 'true',
          definition: node.definition
        });
        
        if (node.children && node.children.length > 0) {
          flattenNodes(node.children);
        }
      });
    };
    
    flattenNodes(nodes);
    this.smqList = flatSMQs;
    this.filteredSMQList = [...flatSMQs];
  }

  addSMQToSelection(): void {
    if (!this.selectedSMQ) {
      this.showSnackBar('Please select an SMQ first');
      return;
    }

    const smqItem = {
      name: this.selectedSMQ.name,
      code: this.selectedSMQ.code,
      type: 'SMQ',
      scope: 'Narrow',
      level: 'SMQ',
      provenance: `SMQ:${this.selectedSMQ.code}-Narrow`,
      medDRAVersion: this.selectedMeddraVersion?.value
    };
    
    this.addToSelection(smqItem);
  }

  // ==================== CMQ METHODS ====================
  private loadCMQList(): void {
    this.loadingCMQs = true;
    this.jsignalsService.getMedraBasketOfSelectedMedraVersion(this.selectedMeddraVersion.value).subscribe({
      next: (baskets: any[]) => {
        this.meddraBaskets = baskets || [];
        this.loadingCMQs = false;
      },
      error: (error) => this.handleError('Error loading CMQs', error)
    });
  }

  loadMeddraBaskets(): void {
    this.spinner.start();
    this.jsignalsService.getMedraBasketOfSelectedMedraVersion(this.selectedMeddraVersion.value).subscribe({
      next: (res: any[]) => {
        this.meddraBaskets = res || [];
        this.spinner.stop();

        if (this.meddraBaskets.length > 0) {
          this.selectedMeddraBasket = this.meddraBaskets[0];
          this.fetchBasketTerms(this.selectedMeddraBasket.id);
        }
      },
      error: (error) => {
        this.spinner.stop();
        this.handleError('Error loading baskets', error);
      }
    });
  }

  onCMQBasketChange(rowData: any): void {
    this.selectedMeddraBasket = rowData;
    if (this.selectedMeddraBasket) {
      this.fetchBasketTerms(this.selectedMeddraBasket.id);
    }
  }

  onMeddraBasketChange(event: any): void {
    if (event.value === 'CREATE_NEW') {
      this.openCreateBasketDialog();
      return;
    }

    this.selectedMeddraBasket = event.value;
    this.fetchBasketTerms(this.selectedMeddraBasket.id);
  }

  private openCreateBasketDialog(): void {
    const dialogRef = this.dialog.open(MeddraBasketCreationComponent, {
      width: '50vw',
      maxWidth: '800px',
      height: '70vh',
      maxHeight: '600px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'basket-creation-dialog',
      data: {
        selectedTerms: this.selectedItems,
        medDraVersion: this.selectedMeddraVersion
      }
    });

    dialogRef.afterClosed().subscribe((newBasket) => {
      if (newBasket) {
        this.loadMeddraBaskets();
      }
    });
  }

  AddtoBasket(): void {
    if (!this.selectedMeddraBasket) {
      this.showSnackBar('Select a MedDRA Basket to add terms');
      return;
    }

    if (!this.selectedItems || this.selectedItems.length === 0) {
      this.showSnackBar('Select items to add into basket');
      return;
    }

    let dataType = null;
    const termsPayload = this.selectedItems.map((item: any) => {
      if (item.type === 'SMQ') {
        dataType = 'SMQ';
        return {
          type: 'SMQ',
          level: 'SMQ',
          code: item.code,
          name: item.name,
          scope: item.scope,
          provenance: item.provenance || 'DIRECT_SMQ',
          hierarchical: item.hierarchical || false,
        };
      } else {
        dataType = 'TERM';
        return {
          type: 'TERM',
          level: item.level,
          code: item.code,
          name: item.name,
          provenance: item.provenance || 'DIRECT_TERM'
        };
      }
    });

    const basketId = this.selectedMeddraBasket.id;
    this.spinner.start();

    this.jsignalsService
  .addTermsToSelectedBasket(basketId, termsPayload, dataType)
  .subscribe({
    next: (res) => {
      this.spinner.stop();

      if (res.error) {
        this.showSnackBar(res.statusMessage || 'Failed to add terms');
        return;
      }

      console.log('Terms added successfully:', res);
      this.showSuccessMessage(termsPayload);
      this.clearSelections();
      this.loadMeddraBaskets();
    },
    error: (err) => {
      console.error('Failed to add items:', err);
      this.spinner.stop();
      this.showSnackBar(err.error?.statusMessage || 'Failed to add items');
    }
  });
  }

  fetchBasketTerms(basketId: number): void {
    this.spinner.start();
    this.jsignalsService.getBasketDetails(basketId).subscribe({
      next: (response: any) => {
        this.BasketData = response || [];
        if (this.cmqGridApi) {
          this.cmqGridApi.setGridOption('rowData', this.BasketData);
        }
        this.spinner.stop();
      },
      error: (err) => {
        console.error("Error loading basket details:", err);
        this.BasketData = [];
        this.spinner.stop();
        this.showSnackBar(err.error?.statusMessage || 'Error loading basket details');
      }
    });
  }

  addCMQToSelection(): void {
    if (!this.selectedMeddraBasket) {
      this.showSnackBar('Please select an CMQ first');
      return;
    }

    const CMQItem = {
      id: this.selectedMeddraBasket.id,
      name: this.selectedMeddraBasket.basketName,
      code: this.selectedMeddraBasket.code,
      type: 'CMQ',
      level: 'CMQ',
      state: this.selectedMeddraBasket.state,
      medDRAVersion: this.selectedMeddraVersion?.value
    };
    
    this.cmqAddToSelection(CMQItem);
  }

  // ==================== SEARCH METHODS ====================
  onSearchInputChange(): void {
    const validation = this.validateSearchInput(this.searchQuery);
    if (!validation.valid && this.searchQuery?.trim().length > 0) {
      this.searchValidationError = validation.message;
    } else {
      this.searchValidationError = '';
    }
  }

  onSearchChange(): void {
    if (!this.searchQuery || this.searchQuery.trim().length < 2) {
      this.clearSearch();
      return;
    }

    // Validate input - only allow alphabetic characters
    const validPattern = /^[a-zA-Z\s]+$/;
    if (!validPattern.test(this.searchQuery.trim())) {
      this.showSnackBar('Please enter only alphabetic characters for search');
      return;
    }

    this.spinner.start();
    
    // Add timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      this.spinner.stop();
      this.showSnackBar('Search request timed out. Please try again.');
    }, 30000);
    
    this.jsignalsService.searchMeddra(this.searchQuery.trim()).subscribe({
      next: (searchResults: any[]) => {
        clearTimeout(timeout);
        this.handleSearchResults(searchResults);
        this.spinner.stop();
      },
      error: (error) => {
        clearTimeout(timeout);
        this.handleError('Search failed', error);
      }
    });
  }

  private validateSearchInput(input: string): { valid: boolean; message: string } {
    if (!input || input.trim().length === 0) {
      return { valid: false, message: '' };
    }
    
    if (input.trim().length < 2) {
      return { valid: false, message: 'Please enter at least 2 characters' };
    }
    
    const validPattern = /^[a-zA-Z\s]+$/;
    if (!validPattern.test(input.trim())) {
      return { 
        valid: false, 
        message: 'Only alphabetic characters (A-Z, a-z) and spaces are allowed' 
      };
    }
    
    return { valid: true, message: '' };
  }

  onSMQSearch(): void {
    if (!this.smqSearchQuery || !this.smqSearchQuery.trim()) {
      this.clearSMQSearch();
      return;
    }
     const validPattern = /^[a-zA-Z\s]+$/;
    if (!validPattern.test(this.smqSearchQuery.trim())) {
      this.showSnackBar('Please enter only alphabetic characters for search');
      return;
    }

    this.searchSMQsByPtTerm();
  }

  onSMQSearchInputChange(): void {
    if (!this.smqSearchQuery?.trim()) {
      this.clearSMQSearch();
    }
  }

  private searchSMQsByPtTerm(): void {
    const searchTerm = this.smqSearchQuery.trim();
    
    if (searchTerm.length < 2) {
      this.showSnackBar('Please enter at least 2 characters');
      return;
    }

    this.loadingSMQs = true;
    
    this.jsignalsService.getSmqsByPtTerm(
      searchTerm,
      this.selectedMeddraVersion?.value,
      this.selectedLanguage.code
    ).subscribe({
      next: (smqNodes: SMQHierarchyNode[]) => {
        this.handleSMQSearchResults(smqNodes, searchTerm);
        this.loadingSMQs = false;
      },
      error: (error) => {
        this.handleError('Error searching SMQs', error);
        this.loadingSMQs = false;
        this.fallbackLocalSearch(searchTerm);
      }
    });
  }

  onCMQSearch(): void {
    if (!this.cmqSearchQuery.trim()) {
      this.loadCMQList();
      return;
    }

    const searchTerm = this.cmqSearchQuery.toLowerCase();
    this.filteredCMQList = this.meddraBaskets.filter(basket =>
      basket.basketName.toLowerCase().includes(searchTerm) ||
      basket.state?.toLowerCase().includes(searchTerm)
    );

    if (this.filteredCMQList.length === 0) {
      this.showSnackBar(`No CMQs found for "${searchTerm}"`);
    } else {
      this.showSnackBar(`Found ${this.filteredCMQList.length} CMQ(s) for "${searchTerm}"`);
    }
  }

  onCMQSearchInputChange(): void {
    if (!this.cmqSearchQuery?.trim()) {
      this.clearCMQSearch();
    }
  }

  private handleSearchResults(results: any[]): void {
    if (results.length === 0) {
      this.treeData = [];
      this.showSnackBar('No results found');
      return;
    }

    const searchTerm = this.searchQuery.toLowerCase();
    const socGroups = this.groupResultsBySOC(results);
    
    this.treeData = socGroups.map(socGroup => {
      const socMatch = socGroup.socTerm.toLowerCase().includes(searchTerm) || 
                       socGroup.socCode.toLowerCase().includes(searchTerm);
      
      const socNode: TreeNode = {
        label: this.createHighlightedLabel(socGroup.socTerm, searchTerm),
        key: socGroup.socCode,
        data: { 
          level: 'SOC', 
          code: socGroup.socCode,
          term: socGroup.socTerm,
          isSearchResult: true,
          searchMatch: socMatch
        },
        expanded: true,
        children: [],
        styleClass: socMatch ? 'search-highlight-node' : ''
      };

      const hlgtGroups = this.groupResultsByHLGT(socGroup.results);
      
      socNode.children = hlgtGroups.map(hlgtGroup => {
        const hlgtMatch = hlgtGroup.hlgtTerm.toLowerCase().includes(searchTerm) || 
                          hlgtGroup.hlgtCode.toLowerCase().includes(searchTerm);
        
        const hlgtNode: TreeNode = {
          label: this.createHighlightedLabel(hlgtGroup.hlgtTerm, searchTerm),
          key: hlgtGroup.hlgtCode,
          data: { 
            level: 'HLGT', 
            code: hlgtGroup.hlgtCode,
            term: hlgtGroup.hlgtTerm,
            isSearchResult: true,
            searchMatch: hlgtMatch
          },
          expanded: true,
          children: [],
          styleClass: hlgtMatch ? 'search-highlight-node' : ''
        };

        const hltGroups = this.groupResultsByHLT(hlgtGroup.results);
        
        hlgtNode.children = hltGroups.map(hltGroup => {
          const hltMatch = hltGroup.hltTerm.toLowerCase().includes(searchTerm) || 
                           hltGroup.hltCode.toLowerCase().includes(searchTerm);
          
          const hltNode: TreeNode = {
            label: this.createHighlightedLabel(hltGroup.hltTerm, searchTerm),
            key: hltGroup.hltCode,
            data: { 
              level: 'HLT', 
              code: hltGroup.hltCode,
              term: hltGroup.hltTerm,
              isSearchResult: true,
              searchMatch: hltMatch
            },
            expanded: true,
            children: [],
            styleClass: hltMatch ? 'search-highlight-node' : ''
          };

          const ptGroups = this.groupResultsByPT(hltGroup.results);
          
          hltNode.children = ptGroups.map(ptGroup => {
            const ptMatch = ptGroup.ptTerm.toLowerCase().includes(searchTerm) || 
                            ptGroup.ptCode.toLowerCase().includes(searchTerm);
            
            const ptNode: TreeNode = {
              label: this.createHighlightedLabel(ptGroup.ptTerm, searchTerm),
              key: ptGroup.ptCode,
              data: { 
                level: 'PT', 
                code: ptGroup.ptCode,
                term: ptGroup.ptTerm,
                isSearchResult: true,
                searchMatch: ptMatch
              },
              expanded: true,
              children: [],
              styleClass: ptMatch ? 'search-highlight-node' : ''
            };

            ptNode.children = ptGroup.results.map((result: any) => {
              const lltMatch = 
                  (result.llt_term?.toLowerCase() || '').includes(searchTerm) || 
                  (result.llt_code?.toLowerCase() || '').includes(searchTerm);
                  
              return {
                label: this.createHighlightedLabel(result.llt_term, searchTerm),
                key: result.llt_code,
                data: { 
                  level: 'LLT', 
                  code: result.llt_code,
                  term: result.llt_term,
                  isSearchResult: true,
                  searchMatch: lltMatch,
                  fullRecord: result
                },
                leaf: true,
                styleClass: lltMatch ? 'search-highlight-node' : ''
              };
            });

            return ptNode;
          });

          return hltNode;
        });

        return hlgtNode;
      });

      return socNode;
    });
  }

  private handleSMQSearchResults(smqNodes: SMQHierarchyNode[], searchTerm: string): void {
    if (smqNodes.length === 0) {
      this.smqTreeData = [];
      this.filteredSMQList = [];
      this.showSnackBar(`No SMQs found for PT term: "${searchTerm}"`);
      return;
    }

    this.smqTreeData = smqNodes.map(smqNode => {
      const isHierarchical = smqNode.has_hierarchy === 'true';
      
      return {
        label: smqNode.name,
        key: smqNode.code,
        data: {
          code: smqNode.code,
          name: smqNode.name,
          has_hierarchy: smqNode.has_hierarchy,
          hierarchical: isHierarchical,
          scope: smqNode.scope || 'N/A',
          definition: smqNode.definition,
          type: 'smq',
          level: 'SMQ',
          searchContext: {
            searchedPtTerm: searchTerm,
            isSearchResult: true
          }
        },
        leaf: !isHierarchical,
        expanded: false,
        children: isHierarchical ? [] : undefined,
        loading: false,
        styleClass: 'search-highlight-node'
      } as SMQTreeNode;
    });

    this.filteredSMQList = smqNodes.map(smqNode => ({
      code: smqNode.code,
      name: smqNode.name,
      scope: smqNode.scope || 'N/A',
      version: this.selectedMeddraVersion?.label,
      hierarchical: smqNode.has_hierarchy === 'true',
      definition: smqNode.definition,
      originalNode: smqNode
    }));

    this.showSnackBar(`Found ${smqNodes.length} SMQ(s) for PT term: "${searchTerm}"`);
  }

  private fallbackLocalSearch(searchTerm: string): void {
    console.warn('Falling back to local search');
    
    const filteredSMQs = this.smqList.filter(smq => 
      smq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      smq.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (filteredSMQs.length === 0) {
      this.smqTreeData = [];
      this.filteredSMQList = [];
      this.showSnackBar(`No SMQs found for: "${searchTerm}"`);
      return;
    }

    this.showSnackBar(`Found ${filteredSMQs.length} SMQ(s) locally for: "${searchTerm}"`);
  }

  // ==================== CLEAR SEARCH METHODS ====================
  clearSearch(): void {
    this.searchQuery = '';
    this.selectedLevel = null;
    this.selectedName = null;
    this.selectedTerm = null;
    this.rightTableData = [];
    this.filteredTableData = [];
    this.searchValidationError = '';
    
    this.reloadFullTree();
    this.showSnackBar('Search cleared, showing all SOCs');
  }

  clearSMQSearch(): void {
    this.smqSearchQuery = '';
    this.selectedSMQ = null;
    this.childSMQsData = [];
    this.meddraTermsData = [];
    
    this.reloadFullSMQTree();
    this.showSnackBar('SMQ search cleared');
  }

  clearCMQSearch(): void {
    this.cmqSearchQuery = '';
    this.filteredCMQList = [...this.meddraBaskets];
    this.loadCMQList();
    this.showSnackBar('Search cleared, showing all CMQs');
  }

  private reloadFullTree(): void {
    this.spinner.start();
    this.treeData = [];
    this.resetTreeSelectionState();
    this.loadSocs();
  }

  private reloadFullSMQTree(): void {
    this.loadingSMQs = true;
    this.smqTreeData = [];
    this.selectedSMQ = null;
    this.loadTopLevelSMQs();
  }

  private resetTreeSelectionState(): void {
    if (this.treeData && this.treeData.length > 0) {
      const clearSelection = (nodes: TreeNode[]) => {
        nodes.forEach(node => {
          node.expanded = false;
          if (node.children && node.children.length > 0) {
            clearSelection(node.children);
          }
        });
      };
      clearSelection(this.treeData);
    }
  }

  // ==================== SELECTION METHODS ====================
  onGridReady(params: any): void {
    this.gridApi = params.api;
  }

  onGridRowSelected(event: any): void {
    if (!event.node.isSelected()) return;

    const rowData = event.data;
    
    if(this.selectedExtractionLevel == 'llt'){
      this.gridApi.forEachNode((node: any) => {
        if (node.data?.fullRecord?.lltCode === rowData.fullRecord?.lltCode) {
          node.setSelected(false);
        }
      });
      this.showSnackBar("Selection of LLT terms is not allowed.");
      return;
    }
    
    const termData = this.extractTermData(rowData, this.selectedExtractionLevel);
    
    const exists = this.selectedItems.some(item => 
      item.code === termData.code && 
      item.level === termData.level &&
      item.type === 'TERM'
    );
    
    if (!exists) {
      this.selectedItems.push(termData);
    }
  }

  onGridSelectionChanged(): void {
    if (!this.gridApi) return;

    const selectedRows = this.gridApi.getSelectedRows();
    this.selectedItems = selectedRows.map(row => 
      this.extractTermData(row, this.selectedExtractionLevel)
    );
  }

  removeSelection(index: number): void {
    const removed = this.selectedItems.splice(index, 1)[0];
    
    if (this.gridApi && removed.fullRecord) {
      this.gridApi.forEachNode((node: any) => {
        if (node.data?.fullRecord?.lltCode === removed.fullRecord?.lltCode) {
          node.setSelected(false);
        }
      });
    }
    
    if (this.smqChildGridApi && removed.type === 'SMQ') {
      this.smqChildGridApi.forEachNode((node: any) => {
        if (node.data?.code === removed.code) {
          node.setSelected(false);
        }
      });
    }
    
    if (this.smqTermsGridApi && removed.type === 'TERM') {
      this.smqTermsGridApi.forEachNode((node: any) => {
        if (node.data?.ptCode === removed.code || node.data?.lltCode === removed.code) {
          node.setSelected(false);
        }
      });
    }
  }

  clearAllSelections(): void {
    this.selectedItems = [];
    this.gridApi?.deselectAll();
    this.smqChildGridApi?.deselectAll();
    this.smqTermsGridApi?.deselectAll();
    this.showSnackBar('All selections cleared');
  }

  // ==================== FILTER METHODS ====================
  onFilterSelectionChange(): void {
    this.includeSecondarySOC = this.selectedFilters.includes('displayCodes');
    
    if (this.gridApi) {
      this.gridApi.resetRowHeights();
      setTimeout(() => {
        this.gridApi!.sizeColumnsToFit();
      }, 50);
    }
    
    this.applyFilters();
  }

  applyFilters(): void {
    let filtered = [...this.rightTableData];
    
    if (this.primarySOCOnly) {
      filtered = filtered.filter(item => item.fullRecord?.primarySoc === true);
    }
    
    if (this.includeNonCurrent) {
      filtered = filtered.filter(item => item.status === 'Non-current');
    }
    
    this.filteredTableData = filtered;
  }

  // ==================== EVENT HANDLERS ====================
  onMeddraVersionChange(): void {
    this.resetData();
    this.loadSocs();
    this.loadTopLevelSMQs();
    this.loadMeddraBaskets();
    this.loadCMQList();
  }

  onLanguageChange(): void {
    const currentExtractionLevel = this.selectedExtractionLevel;
    const currentFilters = [...this.selectedFilters];
    const currentMeddraBasket = this.selectedMeddraBasket;
    
    this.resetData();
    
    this.selectedExtractionLevel = currentExtractionLevel;
    this.selectedFilters = currentFilters;
    this.selectedMeddraBasket = currentMeddraBasket;
    
    this.loadSocs();
    this.loadTopLevelSMQs();
    this.loadCMQList();
    this.loadMeddraBaskets();
    
    this.showSnackBar('Language changed successfully');
  }

  onTabChange(tab: 'terms' | 'smqs' | 'cmqs'): void {
    if (this.activeTab !== tab) {
      if (this.selectedItems.length > 0) {
        this.showSnackBar('Selections cleared due to tab change');
      }

      this.selectedItems = [];
      this.activeTab = tab;
      this.selectedFilters = [];
      this.primarySOCOnly = false;
      this.includeNonCurrent = false;
      this.includeSecondarySOC = false;

      if (tab === 'smqs' && this.smqTreeData.length === 0) {
        this.loadTopLevelSMQs();
      }
      
      if (tab === 'cmqs' && this.meddraBaskets.length === 0) {
        this.loadMeddraBaskets();
      }
    }
  }

  onExtractionLevelChange(): void {
    if (this.gridApi) {
      this.gridApi.deselectAll();
    }
    this.selectedItems = this.selectedItems.filter(item => item.type !== 'TERM');
    this.showSnackBar(`Extraction level changed to ${this.selectedExtractionLevel.toUpperCase()}`);
  }

  // ==================== CMQ STATE METHODS ====================
  isDraft(): boolean {
    return this.selectedMeddraBasket?.state === 'Draft';
  }

  isInReview(): boolean {
    return this.selectedMeddraBasket?.state === 'In Review';
  }

  isApproved(): boolean {
    return this.selectedMeddraBasket?.state === 'Approved';
  }

  submitCMQForReview(): void {
    if (!this.selectedMeddraBasket || !this.selectedMeddraBasket.id) {
      this.showSnackBar("No basket selected.");
      return;
    }

    this.spinner.start();
    this.jsignalsService.updateBasketState(this.selectedMeddraBasket.id, "SUBMIT").subscribe({
      next: (response: any) => {
        this.selectedMeddraBasket.state = "In Review";
        this.loadMeddraBaskets();
        this.spinner.stop();
        this.showSnackBar('Basket submitted for review successfully');
      },
      error: (err) => {
        this.spinner.stop();
        console.error("Submit failed:", err);
        this.showSnackBar('Failed to submit basket for review');
      }
    });
  }

  submitCMQForApproval(): void {
    if (!this.selectedMeddraBasket || !this.selectedMeddraBasket.id) return;

    this.spinner.start();
    this.jsignalsService.updateBasketState(this.selectedMeddraBasket.id, "APPROVE").subscribe({
      next: (res) => {
        this.selectedMeddraBasket.state = "Approved";
        this.loadMeddraBaskets();
        this.spinner.stop();
        this.showSnackBar('Basket approved successfully');
      },
      error: (err) => {
        this.spinner.stop();
        console.error("Approve failed:", err);
        this.showSnackBar('Failed to approve basket');
      }
    });
  }

  rejectCMQ(): void {
    if (!this.selectedMeddraBasket || !this.selectedMeddraBasket.id) return;

    this.spinner.start();
    this.jsignalsService.updateBasketState(this.selectedMeddraBasket.id, "REJECT").subscribe({
      next: (res) => {
        this.selectedMeddraBasket.state = "Rejected";
        this.loadMeddraBaskets();
        this.spinner.stop();
        this.showSnackBar('Basket rejected');
      },
      error: (err) => {
        this.spinner.stop();
        console.error("Reject failed:", err);
        this.showSnackBar('Failed to reject basket');
      }
    });
  }

  retireCMQ(): void {
    if (!this.selectedMeddraBasket || !this.selectedMeddraBasket.id) return;

    this.spinner.start();
    this.jsignalsService.updateBasketState(this.selectedMeddraBasket.id, "RETIRE").subscribe({
      next: (res) => {
        this.selectedMeddraBasket.state = "Retired";
        this.loadMeddraBaskets();
        this.spinner.stop();
        this.showSnackBar('Basket retired successfully');
      },
      error: (err) => {
        this.spinner.stop();
        console.error("Retire failed:", err);
        this.showSnackBar('Failed to retire basket');
      }
    });
  }

  // ==================== SUBMIT METHOD ====================
  onSubmit(): void {
    if (this.selectedItems.length === 0) {
      this.showSnackBar('Please select at least one item');
      return;
    }

    this.dialogRef?.close(this.selectedItems);
  }

  private getFilteredItemsByLevel(level: string): any[] {
    const map = LEVEL_FIELD_MAP[level];
    if (!map) return [];

    const filteredItems = this.selectedItems
      .map(item => item.fullRecord)
      .filter(rec => rec && rec[map.code])
      .map(rec => ({
        term: rec[map.term],
        code: rec[map.code],
        level: level,
        fullRecord: rec
      }));

    return filteredItems;
  }

  // ==================== UI HELPERS ====================
  getSearchPlaceholder(): string {
    return `Search ${this.searchLevelOptions[0]?.label || 'all'} terms`;
  }

  getSMQNodeIcon(node: any): string {
    const isHierarchical = node.data?.has_hierarchy === 'true';
    return isHierarchical ? 'pi pi-sitemap' : 'pi pi-file';
  }

  getSMQNodeIconClass(node: any): string {
    const isHierarchical = node.data?.has_hierarchy === 'true';
    return isHierarchical ? 'hierarchical-icon' : 'leaf-icon';
  }

  // ==================== GRID FORMATTING ====================
  private createTermsColumnDefs(): ColDef[] {
    return [
      {
        headerName: '',
        field: 'selection',
        checkboxSelection: true,
        headerCheckboxSelection: false,
        width: 40,
        sortable: false,
        filter: false,
        resizable: false
       
      },
      { 
        headerName: 'SOC', 
        field: 'soc', 
        ...this.jsignalsService.autoTooltip("SOC"), 
        headerClass: 'level-soc', 
        cellRenderer: (params) => this.formatDisplay(params, 'soc'),
        minWidth: 220,
        flex: 1,
        cellStyle: () => this.getCellStyle(),
        autoHeight: true,
        wrapText: true,
      },
      { 
        headerName: 'HLGT', 
        field: 'hlgt', 
        ...this.jsignalsService.autoTooltip("HLGT"), 
        headerClass: 'level-hlgt', 
        cellRenderer: (params) => this.formatDisplay(params, 'hlgt'),
        minWidth: 200,
        flex: 1,
        cellStyle: () => this.getCellStyle(),
        autoHeight: true,
        wrapText: true,
      },
      { 
        headerName: 'HLT', 
        field: 'hlt', 
        ...this.jsignalsService.autoTooltip("HLT"), 
        headerClass: 'level-hlt', 
        cellRenderer: (params) => this.formatDisplay(params, 'hlt'),
        minWidth: 200,
        flex: 1,
        cellStyle: () => this.getCellStyle(),
        autoHeight: true,
        wrapText: true,
      },
      { 
        headerName: 'PT', 
        field: 'pt',  
        ...this.jsignalsService.autoTooltip("PT"), 
        headerClass: 'level-pt', 
        cellRenderer: (params) => this.formatDisplay(params, 'pt'),
        minWidth: 200,
        flex: 1,
        cellStyle: () => this.getCellStyle(),
        autoHeight: true,
        wrapText: true,
      },
      { 
        headerName: 'LLT', 
        field: 'llt', 
        ...this.jsignalsService.autoTooltip("LLT"), 
        headerClass: 'level-llt', 
        cellRenderer: (params) => this.formatDisplay(params, 'llt'),
        minWidth: 200,
        flex: 1,
        cellStyle: () => this.getCellStyle(),
        autoHeight: true,
        wrapText: true,
      },
    ];
  }

  private createCMQColumnDefs(): ColDef[] {
    return [
      { 
        headerName: 'Name',
        field: 'name',
        ...this.jsignalsService.autoTooltip("Name"),
        flex: 1
      },
      { 
        headerName: 'Level',
        field: 'level',
        ...this.jsignalsService.autoTooltip("Level"),
        flex: 1
      },
      { 
        headerName: 'Code',
        field: 'code',
        ...this.jsignalsService.autoTooltip("Code"),
        flex: 1
      },
      { 
        headerName: 'SMQ Name',
        field: 'sourceSmqName',
        ...this.jsignalsService.autoTooltip("SMQ Name"),
        flex: 1
      },
      { 
        headerName: 'SMQ Scope',
        field: 'smqScope',
        ...this.jsignalsService.autoTooltip("SMQ Scope"),
        flex: 1
      },
      { 
        headerName: 'Created By',
        field: 'addedBy',
        ...this.jsignalsService.autoTooltip("Created By"),
        flex: 1
      },
      { 
        headerName: 'Created At',
        field: 'addedAt',
        ...this.jsignalsService.autoTooltip("Created At"),
        flex: 1
      },
    ];
  }

  private createSMQChildColumnDefs(): ColDef[] {
    return [
      { 
        headerName: 'SMQ Code', 
        field: 'code', 
        width: 120,
        cellClass: 'smq-code-cell',
        ...this.jsignalsService.autoTooltip("SMQ Code")
      },
      { 
        headerName: 'SMQ Name', 
        field: 'name', 
        flex: 1,
        ...this.jsignalsService.autoTooltip("SMQ Name")
      },
      { 
        headerName: 'Hierarchy', 
        field: 'has_hierarchy', 
        flex: 1,
        cellRenderer: (params) => params.value === 'true' ? 'Yes' : 'No',
        cellClass: (params) => params.value === 'true' ? 'hierarchical-cell' : 'leaf-cell',
        ...this.jsignalsService.autoTooltip("Hierarchy")
      },
      { 
        headerName: 'Definition', 
        field: 'definition', 
        flex: 2,
        ...this.jsignalsService.autoTooltip("Definition"),
        cellRenderer: (params) => {
          if (!params.value) return '';
          return params.value.length > 100 ? 
            params.value.substring(0, 100) + '...' : params.value;
        }
      }
    ];
  }

  private createSMQTermsColumnDefs(): ColDef[] {
    return [
      { 
        headerName: 'Name', 
        field: 'name', 
        flex: 1,
        ...this.jsignalsService.autoTooltip("Name")
      },
      { 
        headerName: 'PT Code', 
        field: 'ptCode', 
        width: 100,
        cellClass: 'pt-code-cell',
        ...this.jsignalsService.autoTooltip("PT Code")
      },
      { 
        headerName: 'PT Name', 
        field: 'ptName', 
        flex: 1,
        ...this.jsignalsService.autoTooltip("PT Name")
      },
      { 
        headerName: 'Scope', 
        field: 'scope', 
        width: 100,
        cellRenderer: (params) => {
          if (!params.value) return 'N/A';
          return `<span class="scope-badge ${params.value.toLowerCase()}">${params.value}</span>`;
        },
        ...this.jsignalsService.autoTooltip("Scope")
      }
    ];
  }

  private formatDisplay(params: any, field: string): string {
    const name = params.data?.[field] || '';
    const code = params.data?.fullRecord?.[field + 'Code'] || '';

    if (!this.includeSecondarySOC) {
      return `<div class="term-name-single">${this.escapeHtml(name)}</div>`;
    }

    return `
      <div class="term-with-code-compact">
        <div class="term-name-line">${this.escapeHtml(name)}</div>
        <div class="term-code-line">${this.escapeHtml(code)}</div>
      </div>
    `;
  }

  // ==================== HELPER METHODS ====================
  private buildMeddraRequest(hierarchyLevel: string, filterMap: any = {}): any {
    return {
      filterMap: filterMap,
      primarySOC: this.primarySOCOnly,
      languageOption: this.selectedLanguage.code,
      page: 0,
      medDRAVersion: this.selectedMeddraVersion?.value,
      includeNonCurrent: !this.showCurrentTerms,
      medDRAHierarchyLevel: hierarchyLevel
    };
  }

  private extractLabelAndKey(record: any, level: string): { label: string, key: string } {
    const levelMap: Record<string, { term: string; code: string }> = {
      'SOC': { term: 'socTerm', code: 'socCode' },
      'HLGT': { term: 'hlgtTerm', code: 'hlgtCode' },
      'HLT': { term: 'hltTerm', code: 'hltCode' },
      'PT': { term: 'ptTerm', code: 'ptCode' },
      'LLT': { term: 'lltTerm', code: 'lltCode' }
    };
    
    const mapping = levelMap[level] || levelMap['SOC'];
    return { label: record[mapping.term], key: record[mapping.code] };
  }

  private getNextLevel(currentLevel: string): string {
    const currentIndex = LEVEL_HIERARCHY.indexOf(currentLevel);
    return LEVEL_HIERARCHY[currentIndex + 1] || 'LLT';
  }

  private getBackendLevel(frontendLevel: string): string {
    return BACKEND_LEVEL_MAP[frontendLevel] || 'llt__v';
  }

  private extractTermData(rowData: any, level: string): any {
    const levelMap = LEVEL_FIELD_MAP[level] || LEVEL_FIELD_MAP['llt'];
    
    return {
      fullRecord: rowData.fullRecord || rowData,
      name: rowData[level] || rowData[levelMap.term.replace('Term', '')]?.toLowerCase(),
      code: rowData.fullRecord?.[levelMap.code] || '',
      level: level.toUpperCase(),
      type: 'TERM',
      medDRAVersion: this.selectedMeddraVersion?.label
    };
  }

  private addToSelection(item: any): void {
    const exists = this.selectedItems.some(selected => 
      selected.code === item.code && selected.type === item.type
    );
    
    if (!exists) {
      this.selectedItems.push(item);
      this.showSnackBar('Item added to selection');
    } else {
      this.showSnackBar('Item already selected');
    }
  }

  private cmqAddToSelection(item: any): void {
    const exists = this.selectedItems.some(selected => 
      selected.id === item.id && selected.name === item.name && selected.type === item.type
    );
    
    if (!exists) {
      this.selectedItems.push(item);
      this.showSnackBar('Item added to selection');
    } else {
      this.showSnackBar('Item already selected');
    }
  }

  private clearSelections(): void {
    this.selectedItems = [];
    this.gridApi?.deselectAll();
    this.smqChildGridApi?.deselectAll();
    this.smqTermsGridApi?.deselectAll();
  }

  private showSuccessMessage(termsPayload: any[]): void {
  if (!termsPayload?.length) return;

  const smqCount = termsPayload.filter(item => item.type === 'SMQ').length;
  const termCount = termsPayload.filter(item => item.type === 'TERM').length;
  
  let message = `Added ${termCount} term(s)`;
  if (smqCount > 0) {
    message += ` and ${smqCount} SMQ(s)`;
  }
  message += ` to ${this.selectedMeddraBasket.basketName}`;
  
  this.showSnackBar(message);
}

  private resetData(): void {
    const currentLanguage = this.selectedLanguage;
    const currentExtractionLevel = this.selectedExtractionLevel;
    const currentFilters = [...this.selectedFilters];
    
    this.treeData = [];
    this.rightTableData = [];
    this.filteredTableData = [];
    this.selectedItems = [];
    this.selectedTerm = null;
    this.selectedSMQ = null;
    this.searchQuery = '';
    this.selectedLevel = null;
    this.selectedName = null;
    this.smqTreeData = [];
    this.smqHierarchyNodes = [];
    this.childSMQsData = [];
    this.meddraTermsData = [];
    this.expandedSMQNodes.clear();
    this.cmqSearchQuery = '';
    this.selectedMeddraBasket = null;
    this.BasketData = [];
    
    this.selectedLanguage = currentLanguage;
    this.selectedExtractionLevel = currentExtractionLevel;
    this.selectedFilters = currentFilters;
    this.primarySOCOnly = false;
    this.includeNonCurrent = false;
    this.includeSecondarySOC = false;
  }

  // ==================== ERROR HANDLING ====================
  private handleError(message: string, error?: any): void {
    console.error(`${message}:`, error);
    this.spinner.stop();
    this.showSnackBar(message);
  }

  private showSnackBar(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    let panelClass = '';
    switch(type) {
      case 'success':
        panelClass = 'success-snackbar';
        break;
      case 'error':
        panelClass = 'error-snackbar';
        break;
      default:
        panelClass = 'info-snackbar';
    }
    this.snackBar.open(message, 'Close', { 
      duration: 3000,
      panelClass: panelClass
    });
  }

  // ==================== GROUPING METHODS ====================
  private groupResultsBySOC(results: any[]): any[] {
    const socMap = new Map<string, any>();
    
    results.forEach(result => {
      const socKey = result.soc_code;
      if (!socMap.has(socKey)) {
        socMap.set(socKey, {
          socCode: result.soc_code,
          socTerm: result.soc_term,
          results: [result]
        });
      } else {
        socMap.get(socKey).results.push(result);
      }
    });
    
    return Array.from(socMap.values());
  }

  private groupResultsByHLGT(results: any[]): any[] {
    const hlgtMap = new Map<string, any>();
    
    results.forEach(result => {
      const hlgtKey = result.hlgt_code;
      if (!hlgtMap.has(hlgtKey)) {
        hlgtMap.set(hlgtKey, {
          hlgtCode: result.hlgt_code,
          hlgtTerm: result.hlgt_term,
          results: [result]
        });
      } else {
        hlgtMap.get(hlgtKey).results.push(result);
      }
    });
    
    return Array.from(hlgtMap.values());
  }

  private groupResultsByHLT(results: any[]): any[] {
    const hltMap = new Map<string, any>();
    
    results.forEach(result => {
      const hltKey = result.hlt_code;
      if (!hltMap.has(hltKey)) {
        hltMap.set(hltKey, {
          hltCode: result.hlt_code,
          hltTerm: result.hlt_term,
          results: [result]
        });
      } else {
        hltMap.get(hltKey).results.push(result);
      }
    });
    
    return Array.from(hltMap.values());
  }

  private groupResultsByPT(results: any[]): any[] {
    const ptMap = new Map<string, any>();
    
    results.forEach(result => {
      const ptKey = result.pt_code;
      if (!ptMap.has(ptKey)) {
        ptMap.set(ptKey, {
          ptCode: result.pt_code,
          ptTerm: result.pt_term,
          results: [result]
        });
      } else {
        ptMap.get(ptKey).results.push(result);
      }
    });
    
    return Array.from(ptMap.values());
  }

  private createHighlightedLabel(text: string, searchTerm: string): string {
    if (!searchTerm || !text) return text;
    
    const escapedText = this.escapeHtml(text);
    const regex = new RegExp(`(${this.escapeRegExp(searchTerm)})`, 'gi');
    
    return escapedText.replace(regex, '<span class="search-highlight">$1</span>');
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // ==================== GRID EVENT HANDLERS ====================
  onSMQChildGridReady(params: any): void {
    this.smqChildGridApi = params.api;
  }

  onSMQTermsGridReady(params: any): void {
    this.smqTermsGridApi = params.api;
  }

  onCMQGridReady(params: any): void {
    this.cmqGridApi = params.api;
  }

  onCMQGridRowSelected(params: any): void {
    // Handle CMQ grid row selection if needed
  }
  
  onCMQGridSelectionChanged(): void {
    // Handle CMQ grid selection change if needed
  }

  // ==================== CANCEL METHOD ====================
  cancel(): void {
    this.dialogRef?.close();
  }

  private getCellStyle(): any {
    return {
      'line-height': '1.4',
      'padding': '6px 10px',
      'white-space': 'normal',
      'word-break': 'break-word',
      'overflow-wrap': 'break-word',
      'word-wrap': 'break-word',
      'display': 'block',
      'min-height': '40px',
    };
  }
  displayedRowCount: number = 0;

  cmqColumnDefs: ColDef[] = [
    {
      field: 'basketName',
headerName: '', // or remove column header entirely
      flex: 2,
      filter: true
      
    }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    filterParams: {
        maxNumConditions: 2,
        numAlwaysVisibleConditions: 2,
        defaultJoinOperator: 'OR'
      }
  };


/**
 * @Added By Rahul vk
 * @Date 2024-06-28
 * 
 * This methods added to refresh the tree. As in Prime ng 21 change the tree does 
 * not update on data change, we need to replace the node with new reference to trigger 
 * the change detection and update the tree view
 * 
 * @param nodes 
 * @param targetKey 
 * @param childNodes 
 * @returns 
 */  

  // Add this helper method to your class
private replaceNodeInTree(nodes: TreeNode[], targetKey: string, childNodes: TreeNode[]): TreeNode[] {
    return nodes.map(item => {
      if (item.key === targetKey) {
        return { ...item, children: childNodes, expanded: true, leaf: false };
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: this.replaceNodeInTree(item.children, targetKey, childNodes) };
      }
      return item;
    });
  }

/**
  @Added By Rahul vk
  @Date 2024-06-28
 * 
 * 
 * This methods added to refresh the tree. As in Prime ng 21 change the tree does 
 * not update on data change, we need to replace the node with new reference to trigger 
 * the change detection and update the tree view
 * 
 * @param nodes 
 * @param targetKey 
 * @param childNodes 
 * @param markAsLeaf 
 * @returns 
 */
private replaceSMQNodeInTree(
    nodes: SMQTreeNode[], 
    targetKey: string, 
    childNodes?: SMQTreeNode[],
    markAsLeaf: boolean = false
): SMQTreeNode[] {
    return nodes.map(item => {
        if (item.key === targetKey) {
            if (markAsLeaf) {
                return { 
                    ...item, 
                    leaf: true, 
                    children: undefined, 
                    loading: false,
                    expanded: false
                } as SMQTreeNode;
            }
            return { 
                ...item, 
                children: childNodes, 
                expanded: true, 
                loading: false,
                leaf: childNodes?.length === 0
            } as SMQTreeNode;
        }
        
        if (item.children && item.children.length > 0) {
            return { 
                ...item, 
                children: this.replaceSMQNodeInTree(
                    item.children as SMQTreeNode[], 
                    targetKey, 
                    childNodes,
                    markAsLeaf
                ) 
            } as SMQTreeNode;
        }
        return item;
    });
}

  




}